/**
 * ComfortVignette — the VR comfort vignette AND the blink, as one head-locked mesh (XR P2 comfort slice, 2026-09-28).
 *
 * Why a mesh: the post chain never runs while `renderer.xr.isPresenting`, so a VR-only visual must be scene geometry.
 * The mesh is a band of a sphere (radius VIGNETTE_R, past the VR near plane 0.1) parented to the XR camera, drawn
 * last (renderOrder 1e6), depthTest/depthWrite off — nothing in the world can occlude it. ONE draw per eye.
 *
 * Why per-eye: the alpha is computed in the fragment shader from the fragment's direction in THAT EYE's view space
 * (`positionView`): alpha = 1 − smoothstep(cos(inner + feather), cos(inner), −dir.z). Each eye therefore sees a
 * vignette centred on its own optical axis — like a lens edge, it fuses "at infinity" instead of as a ring floating at
 * 40 cm with a different centre in each eye (a head-centred ring on a near sphere is offset ~5° per eye). The geometry
 * leaves a HOLE in front (the aperture minus a parallax margin) so the open centre of the view is never rasterised:
 * the fill cost is the dark periphery only, and at rest the mesh is hidden (zero cost).
 *
 * The blink (teleport, recenter) is the same mesh with `black` → 1: the hole closes and every fragment is opaque.
 * The per-eye alpha rule is mirrored on the CPU by `vignetteAlpha` (lib/xr_locomotion.ts) — change one, change both.
 */
import * as THREE from 'three/webgpu';
import { positionView, normalize, uniform, float, smoothstep, max, vec3 } from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import {
  vignetteTarget, easeVignette, vignetteAperture, VIGNETTE_VISIBLE_MIN, type RigMotion,
} from '../../lib/xr_locomotion.js';
import type { XRComfort } from '../../lib/xr_comfort.js';

/** Sphere radius (m) around the head centre — beyond the VR near plane (0.1), close enough to stay cheap. */
export const VIGNETTE_R = 0.4;
/** Azimuth segments and polar rings of the band. */
const AZ = 48;
const RINGS = 10;
/** The hole in front is the aperture minus this (degrees): the per-eye parallax at R (asin(0.035/0.4) ≈ 5°) × 2. */
const HOLE_MARGIN_DEG = 12;
/** The band reaches this far back from the forward axis — past every eye's field of view. */
const BAND_END_DEG = 125;

export interface VignetteState { amount: number; target: number; innerDeg: number; featherDeg: number; black: number; visible: boolean; holeDeg: number; attached: boolean }

export class ComfortVignette {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.BufferGeometry;
  private readonly pos: THREE.BufferAttribute;
  private readonly uCosIn = uniform(0.5);
  private readonly uCosOut = uniform(0.3);
  private readonly uAmount = uniform(0);
  private readonly uBlack = uniform(0);
  private holeDeg = -1;
  private amount = 0;
  private target = 0;
  private innerDeg = 64;
  private featherDeg = 16;
  private black = 0;

  constructor() {
    const n = (RINGS + 1) * (AZ + 1);
    this.pos = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
    this.pos.setUsage(THREE.DynamicDrawUsage);
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', this.pos);
    const idx: number[] = [];
    for (let j = 0; j < RINGS; j++) {
      for (let i = 0; i < AZ; i++) {
        const a = j * (AZ + 1) + i, b = a + 1, c = a + (AZ + 1), d = c + 1;
        idx.push(a, c, b, b, c, d);
      }
    }
    this.geo.setIndex(idx);
    // A zero normal (THE MISSING-ATTRIBUTE LAW: a node material may read `normal` under an MRT scene pass; the
    // vignette is never drawn outside XR, but a geometry without the attribute must not be able to black a frame).
    this.geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    this.rebuild(0);

    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    mat.fog = false;
    mat.colorNode = vec3(0, 0, 0);
    const c = normalize(positionView).z.negate(); // cosine to THIS eye's own axis
    const ring = float(1).sub(smoothstep(this.uCosOut, this.uCosIn, c)).mul(this.uAmount);
    mat.opacityNode = max(ring, this.uBlack);
    mat.name = 'xr-comfort-vignette';
    markMrtOverlay(mat); // a transparent overlay never rewrites the surface targets (the overlay law)
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.name = 'xr-comfort-vignette';
    this.mesh.renderOrder = 1e6;
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.matrixAutoUpdate = true;
  }

  /** Place the band's rings from the hole's edge to BAND_END_DEG (denser near the hole, where the feather lives). */
  private rebuild(holeDeg: number): void {
    this.holeDeg = holeDeg;
    const a = this.pos.array as Float32Array;
    const t0 = (holeDeg * Math.PI) / 180, t1 = (BAND_END_DEG * Math.PI) / 180;
    for (let j = 0; j <= RINGS; j++) {
      const f = Math.pow(j / RINGS, 1.6);
      const th = t0 + (t1 - t0) * f;
      const st = Math.sin(th), ct = Math.cos(th);
      for (let i = 0; i <= AZ; i++) {
        const ph = (i / AZ) * Math.PI * 2;
        const o = (j * (AZ + 1) + i) * 3;
        a[o] = VIGNETTE_R * st * Math.cos(ph);
        a[o + 1] = VIGNETTE_R * st * Math.sin(ph);
        a[o + 2] = -VIGNETTE_R * ct;
      }
    }
    this.pos.needsUpdate = true;
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), VIGNETTE_R);
  }

  /** Parent under the XR camera (idempotent). */
  attach(camera: THREE.Object3D): void {
    if (this.mesh.parent !== camera) camera.add(this.mesh);
  }

  detach(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.visible = false;
    this.amount = 0; this.target = 0; this.black = 0;
  }

  /**
   * One frame: ease toward the motion's target, set the blink, place the aperture. `motion` null = rest (a jump frame,
   * a menu) — the vignette opens. Returns whether the mesh draws this frame.
   */
  update(dt: number, motion: RigMotion | null, c: Pick<XRComfort, 'vignette' | 'vehicleComfort'>, black: number): boolean {
    this.target = motion ? vignetteTarget(motion, c) : 0;
    this.amount = easeVignette(this.amount, this.target, dt);
    const riding = !!motion?.riding;
    const ap = vignetteAperture(this.amount, riding, c.vehicleComfort);
    this.innerDeg = ap.innerDeg;
    this.featherDeg = ap.featherDeg;
    this.black = Math.max(0, Math.min(1, black));
    this.uCosIn.value = Math.cos((this.innerDeg * Math.PI) / 180);
    this.uCosOut.value = Math.cos((Math.min(179, this.innerDeg + this.featherDeg) * Math.PI) / 180);
    // The ring's strength rises with the amount so the first degrees of closing are faint (no hard edge popping in).
    this.uAmount.value = Math.min(1, this.amount * 1.6);
    this.uBlack.value = this.black;
    const hole = this.black > 0.001 ? 0 : Math.max(0, this.innerDeg - HOLE_MARGIN_DEG);
    if (Math.abs(hole - this.holeDeg) > 0.5) this.rebuild(hole);
    const show = this.amount > VIGNETTE_VISIBLE_MIN || this.black > 0.001;
    this.mesh.visible = show;
    return show;
  }

  state(): VignetteState {
    return {
      amount: +this.amount.toFixed(3), target: +this.target.toFixed(3), innerDeg: +this.innerDeg.toFixed(1),
      featherDeg: this.featherDeg, black: +this.black.toFixed(3), visible: this.mesh.visible, holeDeg: +this.holeDeg.toFixed(1),
      attached: !!this.mesh.parent,
    };
  }

  dispose(): void {
    this.detach();
    this.geo.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
